import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertOctagon, ChevronRight, Flag, Image, ScrollText } from "lucide-react";
import { api, timeAgo } from "@/lib/api";
import { useAdmin } from "./useAdmin";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";
import { Progress } from "@/components/ui/progress";

const QUEUE_LABEL = { normal: "Normal", priority: "Priority", compliance: "Compliance", all: "All" };
const STATUS_LABEL = { open: "Open", decided: "Decided", all: "All" };
const SUBJECT_ICON = { listing: ScrollText, story: Image, report: Flag };

function riskColor(band) {
  if (band === "held" || band === "high") return "bg-[#B42318]";
  if (band === "medium") return "bg-[#9A6B12]";
  return "bg-[#1F7A4D]";
}

export default function AdminCasesPage() {
  useSeo({ title: "Moderation queue | LUX", ...PRIVATE_SEO });
  const { can } = useAdmin();
  const [params, setParams] = useSearchParams();
  const queue = params.get("queue") || "normal";
  const status = params.get("status") || "open";
  const subjectType = params.get("subject_type") || "";
  const queries = useMemo(() => {
    const q = new URLSearchParams();
    q.set("queue", queue);
    q.set("status", status);
    if (subjectType) q.set("subject_type", subjectType);
    return q.toString();
  }, [queue, status, subjectType]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["admin", "cases", queries],
    queryFn: async () => (await api.get(`/admin/cases?${queries}`)).data,
    keepPreviousData: true,
  });

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v) next.delete(k); else next.set(k, v);
    setParams(next, { replace: true });
  };

  const queues = can("cases.compliance") ? ["normal", "priority", "compliance", "all"] : ["normal", "priority", "all"];

  return (
    <div className="space-y-5" data-testid="admin-cases-page">
      <header>
        <p className="adm-label">Moderation</p>
        <h1 className="mt-2 text-3xl font-semibold text-admin-text">Review queue</h1>
        <p className="mt-1 text-sm text-admin-muted">Highest risk cases appear first. Approvals, requests for changes and suspensions take effect immediately.</p>
      </header>

      <div className="flex flex-wrap items-center gap-3" data-testid="admin-cases-filters">
        <div className="flex gap-1 rounded-full border border-admin-border bg-admin-surface p-1" role="tablist" aria-label="Queue">
          {queues.map((q) => (
            <button key={q} onClick={() => setParam("queue", q)}
              className={`rounded-full px-3 py-1.5 text-sm transition-colors ${queue === q ? "bg-[#111827] text-white" : "text-admin-text hover:bg-admin-surface2"}`}
              data-testid={`admin-cases-queue-${q}`}>
              {QUEUE_LABEL[q]}
            </button>
          ))}
        </div>
        <div className="flex gap-1 rounded-full border border-admin-border bg-admin-surface p-1" role="tablist" aria-label="Status">
          {["open", "decided", "all"].map((s) => (
            <button key={s} onClick={() => setParam("status", s)}
              className={`rounded-full px-3 py-1.5 text-sm transition-colors ${status === s ? "bg-[#111827] text-white" : "text-admin-text hover:bg-admin-surface2"}`}
              data-testid={`admin-cases-status-${s}`}>
              {STATUS_LABEL[s]}
            </button>
          ))}
        </div>
        <div className="flex gap-1 rounded-full border border-admin-border bg-admin-surface p-1" aria-label="Type">
          {[["", "All types"], ["listing", "Listings"], ["story", "Stories"], ["report", "Reports"]].map(([v, l]) => (
            <button key={v} onClick={() => setParam("subject_type", v)}
              className={`rounded-full px-3 py-1.5 text-sm transition-colors ${subjectType === v ? "bg-[#111827] text-white" : "text-admin-text hover:bg-admin-surface2"}`}
              data-testid={`admin-cases-subject-${v || "all"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <section className="adm-card overflow-hidden" data-testid="admin-moderation-queue-table">
        {isLoading ? (
          <p className="p-6 text-sm text-admin-muted">Loading cases...</p>
        ) : !data?.cases?.length ? (
          <div className="flex flex-col items-center px-6 py-16 text-center" data-testid="admin-cases-empty">
            <AlertOctagon className="h-7 w-7 text-admin-muted" aria-hidden="true" />
            <p className="mt-4 text-lg font-semibold text-admin-text">No cases match these filters</p>
            <p className="mt-1 text-sm text-admin-muted">Try switching queue or status.</p>
          </div>
        ) : (
          <ul className={`divide-y divide-admin-border ${isFetching ? "opacity-70" : ""}`}>
            {data.cases.map((c) => {
              const Icon = SUBJECT_ICON[c.subject_type] || Flag;
              return (
                <li key={c.id}>
                  <Link to={`/admin/cases/${c.id}`} className="flex items-center gap-4 px-4 py-4 hover:bg-admin-surface2 sm:px-5" data-testid="admin-moderation-queue-row">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-admin-surface2 text-admin-muted">
                      <Icon className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-semibold text-admin-text">{c.subject_name}</p>
                        {c.is_demo && <span className="adm-pill border-admin-border bg-admin-surface2 text-admin-muted">Demo</span>}
                        {c.blocked && <span className="adm-pill border-[#F2B4B4] bg-[#FEECEC] text-[#B42318]">Blocked</span>}
                        {c.status !== "open" && c.status !== "escalated" && (
                          <span className="adm-pill border-admin-border bg-admin-surface2 text-admin-muted">{c.status.replace(/_/g, " ")}</span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-admin-muted">
                        <span>#{c.number}</span>
                        <span>{c.subject_type}</span>
                        <span>{timeAgo(c.created_at)}</span>
                        {c.report_count > 0 && <span className="text-[#B42318]">{c.report_count} report{c.report_count === 1 ? "" : "s"}</span>}
                      </div>
                      {c.signals?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {c.signals.slice(0, 3).map((s, i) => (
                            <span key={i} className="adm-pill border-admin-border bg-admin-surface2 text-admin-text" title={`+${s.points}`}>
                              {s.label}
                            </span>
                          ))}
                          {c.signals.length > 3 && <span className="text-xs text-admin-muted">+{c.signals.length - 3}</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex w-32 shrink-0 flex-col items-end gap-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-admin-text" data-testid="admin-moderation-risk-score">{c.score}</span>
                        <span className="adm-pill border-admin-border bg-admin-surface2 text-admin-muted uppercase">{c.band}</span>
                      </div>
                      <div className="h-1.5 w-28 overflow-hidden rounded-full bg-admin-surface2">
                        <div className={`h-full ${riskColor(c.band)}`} style={{ width: `${Math.min(100, c.score)}%` }} />
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-admin-muted" aria-hidden="true" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
